import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CheckCircle2, XCircle, Clock, ArrowLeft, ArrowRight, Send, Loader,
  Trophy, ThumbsDown, MoonStar, Hourglass, Sparkles, Plus, X,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * InterviewDebrief — the 30-second "How did it go?" form fired ~24h after a
 * scheduled interview. Atomically:
 *   (1) marks the interview as completed,
 *   (2) updates the linked analysis's Outcome (when tied),
 *   (3) creates a Stepping Stone journal entry.
 *
 * Built to be punchy: one big outcome pick (4 emoji tiles), a few optional
 * follow-up fields, submit → toast → /interviews/:id.
 */
const STATUS_TILES = [
  { value: "next_round", label: "Moved to next round", Icon: ArrowRight, color: "#1E40AF", soft: "#DBEAFE" },
  { value: "offer",      label: "Got an offer",         Icon: Trophy,    color: "#047857", soft: "#D1FAE5" },
  { value: "rejection",  label: "Rejected",             Icon: ThumbsDown, color: "#B91C1C", soft: "#FEE2E2" },
  { value: "no_response",label: "No response yet",      Icon: Hourglass, color: "#CA8A04", soft: "#FEF3C7" },
];

export default function InterviewDebrief() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [stage, setStage] = useState("");
  const [salary, setSalary] = useState("");
  const [notes, setNotes] = useState("");
  const [wins, setWins] = useState([""]);
  const [focusAreas, setFocusAreas] = useState([""]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/interviews/${id}`);
        setInterview(data);
        if (data.debrief_submitted_at) {
          // Already submitted — show the post-submit state
          setStatus(data.debrief?.outcome_status || "");
        }
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load interview.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const updateAt = (list, i, val) => list.map((v, idx) => (idx === i ? val : v));
  const addRow = (setter) => (list) => () => setter([...list, ""]);
  const removeRow = (setter) => (list) => (i) => () => setter(list.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!status) {
      setError("Pick how the interview went first.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        outcome_status: status,
        next_round_stage: stage || null,
        salary_signal: salary || null,
        quick_notes: notes || null,
        wins: wins.filter((w) => w && w.trim()),
        focus_areas: focusAreas.filter((w) => w && w.trim()),
      };
      const { data } = await api.post(`/interviews/${id}/debrief`, payload);
      // Brief celebration, then redirect to the interview detail
      navigate(`/interviews/${id}?debrief=done&stone=${data.stepping_stone_id}`);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Couldn't save debrief.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
          <div className="rm-card-static p-6 flex items-center gap-2">
            <Loader size={16} className="animate-spin" style={{ color: "#FF6A00" }} />
            <span className="font-mono-rm text-[11px] uppercase tracking-widest">Loading…</span>
          </div>
        </div>
      </div>
    );
  }

  if (!interview) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
          <div className="rm-card-static p-6">
            <h2 className="font-display text-xl uppercase mb-2">Not found</h2>
            <p className="text-sm">{error || "Couldn't find this interview."}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen rm-paper" data-testid="interview-debrief-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 md:py-10">
        <button
          type="button"
          onClick={() => navigate(`/interviews/${id}`)}
          className="font-mono-rm uppercase text-[10px] tracking-widest mb-3 inline-flex items-center gap-1"
          style={{ color: "#475569", fontWeight: 700 }}
          data-testid="debrief-back-btn"
        >
          <ArrowLeft size={11} /> Back to interview
        </button>

        {/* Header */}
        <div
          className="rm-card-static p-5 md:p-7 mb-5"
          style={{
            background: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)",
            borderColor: "#047857",
            boxShadow: "5px 5px 0 0 #047857",
          }}
        >
          <div className="font-mono-rm uppercase text-[10px] tracking-widest"
               style={{ color: "#047857", fontWeight: 700 }}>
            · 30-second debrief · Resmo
          </div>
          <h1 className="font-display uppercase leading-tight mt-1 mb-2"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)", color: "#064E3B", letterSpacing: "-0.02em" }}
              data-testid="debrief-title">
            How did the <span style={{
              background: "linear-gradient(135deg, #047857 0%, #064E3B 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              backgroundClip: "text", color: "#047857",
            }}>{interview.role_company}</span> interview go?
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: "#064E3B" }}>
            Your answers auto-create a Stepping Stone journal entry and (if this
            interview was tied to a tailored resume) update your outcome tracker —
            so the patterns surface in your next role.
          </p>
        </div>

        {interview.debrief_submitted_at && (
          <div
            className="rm-card-static p-4 mb-5"
            style={{ background: "#FFFBEB", borderColor: "#CA8A04" }}
            data-testid="debrief-already-banner"
          >
            <div className="flex items-start gap-2">
              <CheckCircle2 size={18} style={{ color: "#047857", flexShrink: 0, marginTop: 2 }} />
              <div>
                <div className="font-display uppercase text-sm leading-tight" style={{ color: "#0F1B2D" }}>
                  Already debriefed
                </div>
                <p className="text-[12px] mt-1" style={{ color: "#475569" }}>
                  You already logged a debrief for this interview. Submitting again
                  will overwrite it.
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-5" data-testid="debrief-form">
          {/* Status tiles */}
          <fieldset>
            <legend className="rm-label text-[11px] mb-2">Outcome</legend>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="debrief-status-tiles">
              {STATUS_TILES.map((t) => {
                const active = status === t.value;
                const Icon = t.Icon;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setStatus(t.value)}
                    className="rm-card-static p-3 text-left transition-transform hover:-translate-y-0.5"
                    style={{
                      background: active ? t.soft : "white",
                      borderColor: active ? t.color : "#0F1B2D",
                      boxShadow: active
                        ? `4px 4px 0 0 ${t.color}`
                        : "3px 3px 0 0 #CBD5E1",
                    }}
                    data-testid={`debrief-status-${t.value}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={18} style={{ color: t.color }} />
                      <div className="font-display uppercase text-[14px] leading-tight"
                           style={{ color: "#0F1B2D", letterSpacing: "-0.01em" }}>
                        {t.label}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {status === "next_round" && (
            <div>
              <label className="rm-label text-[11px]">Next stage</label>
              <input
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                placeholder="On-site · Final round · Hiring manager loop"
                className="rm-input w-full"
                data-testid="debrief-stage"
              />
            </div>
          )}

          {status === "offer" && (
            <div>
              <label className="rm-label text-[11px]">Offer / Salary signal (optional)</label>
              <input
                value={salary}
                onChange={(e) => setSalary(e.target.value)}
                placeholder="$180K base + 10% bonus"
                className="rm-input w-full"
                data-testid="debrief-salary"
              />
            </div>
          )}

          {/* Wins + focus areas — minimal, dynamic */}
          <DynamicList
            label="What worked? (Wins)"
            items={wins}
            onChange={(i, v) => setWins(updateAt(wins, i, v))}
            onAdd={() => setWins([...wins, ""])}
            onRemove={(i) => setWins(wins.filter((_, idx) => idx !== i))}
            placeholder="Anchored every answer in real customer impact"
            testid="debrief-wins"
            accent="#047857"
          />
          <DynamicList
            label="What needs work? (Focus areas)"
            items={focusAreas}
            onChange={(i, v) => setFocusAreas(updateAt(focusAreas, i, v))}
            onAdd={() => setFocusAreas([...focusAreas, ""])}
            onRemove={(i) => setFocusAreas(focusAreas.filter((_, idx) => idx !== i))}
            placeholder="Practice the salary conversation"
            testid="debrief-focus"
            accent="#B91C1C"
          />

          <div>
            <label className="rm-label text-[11px]">Quick notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="They seemed most curious about the migration story. Brought up internal politics around the platform team."
              className="rm-input w-full"
              data-testid="debrief-notes"
            />
          </div>

          {error && (
            <div className="rm-card-static p-2 text-xs font-mono-rm"
                 style={{ background: "#FEE2E2", borderColor: "#B91C1C", color: "#991B1B" }}
                 data-testid="debrief-error">
              · {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => navigate(`/interviews/${id}`)}
              className="rm-btn rm-btn-ghost text-xs px-3 py-2"
              data-testid="debrief-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !status}
              className="rm-btn rm-btn-primary text-sm px-4 py-2.5"
              data-testid="debrief-submit-btn"
            >
              {submitting ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
              {submitting ? "Saving…" : "Save debrief"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DynamicList({ label, items, onChange, onAdd, onRemove, placeholder, testid, accent }) {
  return (
    <fieldset data-testid={testid}>
      <legend className="rm-label text-[11px] mb-1">{label}</legend>
      <div className="space-y-2">
        {items.map((v, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input
              value={v}
              onChange={(e) => onChange(i, e.target.value)}
              placeholder={placeholder}
              className="rm-input flex-1"
              data-testid={`${testid}-input-${i}`}
            />
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="rm-btn rm-btn-ghost text-[10px] px-2 py-2"
                data-testid={`${testid}-remove-${i}`}
              >
                <X size={11} />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={onAdd}
          className="rm-btn rm-btn-ghost text-[10px] px-2 py-1.5"
          style={{ color: accent, borderColor: accent }}
          data-testid={`${testid}-add`}
        >
          <Plus size={11} /> Add another
        </button>
      </div>
    </fieldset>
  );
}
